import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  Container,
  FormControl,
  InputLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  TableContainer,
  Button,
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  TrendingUp,
  People,
  Description,
  AttachMoney,
  CalendarToday,
  Business,
  Add,
  Assessment,
  CloudUpload,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '../../contexts/CompanyContext';
import { EDEFTER_API_URL } from '../../config/edefterApi';
import AuthService from '../../services/authService';

const EDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { selectedCompany, companies, loadingCompanies, taxId } = useCompany();

  const [selectedPeriod, setSelectedPeriod] = useState('2024');
  const [selectedCustomer, setSelectedCustomer] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for data
  const [kpiData, setKpiData] = useState([]);
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [customerRevenue, setCustomerRevenue] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [topCustomerDetails, setTopCustomerDetails] = useState([]);

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  // useCallback ile fetchDashboardData'yı sarmalayarak sonsuz döngüyü önlüyoruz
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        period: selectedPeriod,
        customer: selectedCustomer,
      });
      // Seçili şirkete göre filtre: backend sadece bu identifier (vergi no) ile eşleşen belgeleri döner
      const identifier = taxId || selectedCompany?.vergiKimlikNo || selectedCompany?.taxNumber;
      if (identifier) {
        params.set('identifier', String(identifier).trim());
      }

      const apiUrl = `${EDEFTER_API_URL}/api/edefter/dashboard?${params}`;
      
      const response = await AuthService.authenticatedFetch(apiUrl);

      if (!response) {
        throw new Error('Session expired. Please sign in again.');
      }
      
      if (!response.ok) {
        let errorText = '';
        try {
          errorText = await response.text();
        } catch (e) {
          errorText = 'Could not read response text';
        }
        console.error('Dashboard API error response:', errorText);
        throw new Error(`Failed to load dashboard data: ${response.status} ${response.statusText}${errorText ? ' - ' + errorText : ''}`);
      }

      const data = await response.json();
      
      // Hata kontrolü
      if (data.error) {
        throw new Error(data.error);
      }
      
      // KPI verilerini formatla (backend'den gelen value sayısal, frontend string bekliyor)
      // Translate Turkish KPI titles to English
      const kpiTitleMap = {
        'Toplam Satış': 'Total Sales',
        'Müşteri Sayısı': 'Customer Count',
        'Belge Sayısı': 'Document Count',
        'Ortalama İşlem': 'Average Transaction'
      };
      
      const formattedKpiData = (data.kpiData || []).map(kpi => {
        const englishTitle = kpiTitleMap[kpi.title] || kpi.title;
        return {
          ...kpi,
          title: englishTitle,
          value: typeof kpi.value === 'number' 
            ? (kpi.title === 'Toplam Satış' || kpi.title === 'Ortalama İşlem' 
                ? formatCurrency(kpi.value) 
                : kpi.value.toString())
            : kpi.value
        };
      });
      
      setKpiData(formattedKpiData);
      setMonthlyRevenue(data.monthlyRevenue || []);
      setCustomerRevenue(data.customerRevenue || []);
      setDocumentTypes(data.documentTypes || []);
      setCustomers(data.customers || []);
      setTopCustomerDetails(data.topCustomerDetails || []);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod, selectedCustomer, taxId, selectedCompany?.vergiKimlikNo, selectedCompany?.taxNumber]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return '₺0';
    }
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const kpiIcons = {
    'Total Sales': AttachMoney,
    'Customer Count': People,
    'Document Count': Description,
    'Average Transaction': TrendingUp,
    // Turkish fallbacks
    'Toplam Satış': AttachMoney,
    'Müşteri Sayısı': People,
    'Belge Sayısı': Description,
    'Ortalama İşlem': TrendingUp,
  };

  const kpiColors = {
    'Total Sales': '#3b82f6',
    'Customer Count': '#10b981',
    'Document Count': '#f59e0b',
    'Average Transaction': '#8b5cf6',
    // Turkish fallbacks
    'Toplam Satış': '#3b82f6',
    'Müşteri Sayısı': '#10b981',
    'Belge Sayısı': '#f59e0b',
    'Ortalama İşlem': '#8b5cf6',
  };

  // Eğer company yoksa veya seçilmemişse, sadece 2 kart göster
  const hasNoCompany = !loadingCompanies && (!companies || companies.length === 0);
  const noCompanySelected = !selectedCompany;

  if (hasNoCompany || noCompanySelected) {
  
    const actionCards = [
      {
        id: 1,
        title: 'Create Company Profile',
        subtitle: 'Add Company',
        description: 'Set up your company with tax information and NACE code for seamless data management.',
        icon: Add,
        color: 'rgba(16, 38, 166, 1)',
        gradient: null,
        path: '/edefter/add-company',
        disableHover: false,
      },
      {
        id: 2,
        title: 'Upload XML File',
        subtitle: 'Upload General Journal',
        description: 'Import your XML general journal to auto-generate trial balance and analytics.',
        icon: CloudUpload,
        color: '#10b981',
        gradient: null,
        path: '/edefter/upload',
        disableHover: false,
      },
    ];
  
    const features = [
      {
        icon: TrendingUp,
        title: 'Real-time Analytics',
        description: 'Track your financial metrics instantly',
      },
      {
        icon: Assessment,
        title: 'Automated Reports',
        description: 'Generate comprehensive financial reports',
      },
    ];
  
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)',
          py: { xs: 3, sm: 4, md: 6 },
          px: { xs: 2, sm: 3 },
        }}
      >
        <Container maxWidth="lg">
          {/* Hero Section */}
          <Box
            sx={{
              textAlign: 'center',
              mb: { xs: 4, sm: 6, md: 8 },
              animation: 'fadeIn 0.6s ease-in',
              '@keyframes fadeIn': {
                from: { opacity: 0, transform: 'translateY(-20px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            <Typography
              variant={isMobile ? 'h4' : 'h3'}
              sx={{
                fontWeight: 800,
                color: 'rgba(16, 38, 166, 1)',
                mb: 2,
                letterSpacing: '-0.02em',
              }}
            >
              Welcome to Fidost
            </Typography>
            <Typography
              variant={isMobile ? 'body1' : 'h6'}
              color="text.secondary"
              sx={{
                maxWidth: '600px',
                mx: 'auto',
                fontWeight: 400,
                lineHeight: 1.6,
              }}
            >
              {hasNoCompany
                ? 'Start your financial journey by setting up your first company profile'
                : 'Select a company from the header to access your analytics dashboard'}
            </Typography>
          </Box>
  
          {/* Action Cards Grid */}
          <Box
            sx={{
              display: 'grid',
              gap: { xs: 3, sm: 4 },
              mb: { xs: 4, sm: 6 },
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, 1fr)',
              },
            }}
          >
            {actionCards.map((card, index) => {
              const IconComponent = card.icon;
              return (
                <Card
                  key={card.id}
                  onClick={() => navigate(card.path)}
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    animation: `slideUp 0.6s ease-out ${index * 0.1}s backwards`,
                    '@keyframes slideUp': {
                      from: { opacity: 0, transform: 'translateY(30px)' },
                      to: { opacity: 1, transform: 'translateY(0)' },
                    },
                    ...(card.disableHover ? {} : {
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
                        '& .icon-box': {
                          transform: 'scale(1.1) rotate(5deg)',
                        },
                        ...(card.gradient && {
                          '& .card-gradient': {
                            opacity: 0.15,
                          },
                        }),
                      },
                      '&:active': {
                        transform: 'translateY(-4px)',
                      },
                    }),
                  }}
                >
                  {/* Background Gradient Overlay */}
                  {card.gradient && (
                    <Box
                      className="card-gradient"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '100%',
                        height: '100%',
                        background: card.gradient,
                        opacity: 0.08,
                        transition: 'opacity 0.3s ease',
                        pointerEvents: 'none',
                      }}
                    />
                  )}
  
                  <CardContent
                    sx={{
                      p: { xs: 3, sm: 4 },
                      position: 'relative',
                      minHeight: { xs: 200, sm: 240 },
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {/* Icon */}
                    <Box
                      className="icon-box"
                      sx={{
                        width: { xs: 56, sm: 64 },
                        height: { xs: 56, sm: 64 },
                        borderRadius: 2.5,
                        backgroundColor: card.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        transition: card.disableHover ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: `0 8px 24px ${card.color}40`,
                      }}
                    >
                      <IconComponent
                        sx={{
                          color: 'white',
                          fontSize: { xs: 28, sm: 32 },
                        }}
                      />
                    </Box>
  
                    {/* Subtitle */}
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        letterSpacing: '0.05em',
                        mb: 1,
                      }}
                    >
                      {card.subtitle}
                    </Typography>
  
                    {/* Title */}
                    <Typography
                      variant={isMobile ? 'h6' : 'h5'}
                      sx={{
                        fontWeight: 700,
                        color: 'text.primary',
                        mb: 2,
                        lineHeight: 1.3,
                      }}
                    >
                      {card.title}
                    </Typography>
  
                    {/* Description */}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        lineHeight: 1.7,
                        flex: 1,
                      }}
                    >
                      {card.description}
                    </Typography>
  
                    {/* Arrow Indicator */}
                    <Box
                      sx={{
                        mt: 2,
                        display: 'flex',
                        alignItems: 'center',
                        color: card.color,
                        fontWeight: 600,
                        fontSize: '0.875rem',
                      }}
                    >
                      Get Started
                      <Box
                        component="span"
                        sx={{
                          ml: 1,
                          transition: 'transform 0.3s ease',
                          display: 'inline-block',
                          '.MuiCard-root:hover &': {
                            transform: 'translateX(4px)',
                          },
                        }}
                      >
                        →
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
  
          {/* Features Section */}
          {!isTablet && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 3,
                animation: 'fadeIn 0.8s ease-in 0.3s backwards',
              }}
            >
              {features.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <Card
                    key={index}
                    sx={{
                      borderRadius: 2,
                      p: 3,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      background: 'rgba(255, 255, 255, 0.7)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.5)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                        '& .feature-icon-box': {
                          transform: 'scale(1.1)',
                        },
                      },
                    }}
                  >
                    <Box
                      className="feature-icon-box"
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 1.5,
                        backgroundColor: 'rgba(16, 38, 166, 1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0.9,
                        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: '0 4px 12px rgba(16, 38, 166, 0.3)',
                      }}
                    >
                      <IconComponent sx={{ color: 'white', fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600} mb={0.5}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </Box>
                  </Card>
                );
              })}
            </Box>
          )}
        </Container>
      </Box>
    );
  }
  if (loading || loadingCompanies) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 1.5, sm: 2, md: 3 }, 
      backgroundColor: '#f5f7fa', 
      minHeight: '100vh' 
    }}>
      <Box sx={{ 
        maxWidth: '1400px', 
        mx: 'auto',
        px: { xs: 1, sm: 2 }
      }}>
        {/* Header */}
        <Box mb={{ xs: 2, sm: 3 }}>
          <Typography 
            variant={isMobile ? 'h5' : 'h4'} 
            sx={{ 
              fontWeight: 700, 
              color: 'text.primary', 
              mb: 1,
              fontSize: { xs: '1.5rem', sm: '2rem' }
            }}
          >
            Sales & Marketing Analysis
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            Customer and period-based performance tracking
          </Typography>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                  <InputLabel>Period</InputLabel>
                  <Select
                    value={selectedPeriod}
                    onChange={(e) => setSelectedPeriod(e.target.value)}
                    label="Period"
                  >
                    <MenuItem value="2024">2024</MenuItem>
                    <MenuItem value="2023">2023</MenuItem>
                    <MenuItem value="2022">2022</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                  <InputLabel>Customer</InputLabel>
                  <Select
                    value={selectedCustomer}
                    onChange={(e) => setSelectedCustomer(e.target.value)}
                    label="Customer"
                  >
                    {customers.map((customer) => (
                      <MenuItem key={customer.id} value={customer.id}>
                        {customer.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* KPI Cards */}
        <Box
          sx={{
            display: 'grid',
            gap: { xs: 2, md: 3 },
            mb: { xs: 2, sm: 3 },
            gridTemplateColumns: {
              xs: 'repeat(1, minmax(0, 1fr))',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(4, minmax(0, 1fr))',
            },
            gridAutoRows: { xs: 'minmax(180px, auto)', md: 'minmax(220px, auto)' },
            alignItems: 'stretch',
          }}
        >
          {kpiData.map((kpi, idx) => {
            const IconComponent = kpiIcons[kpi.title] || TrendingUp;
            const color = kpiColors[kpi.title] || '#3b82f6';
            
            return (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  width: '100%',
                  height: '100%',
                  minHeight: { xs: 180, md: 220 },
                }}
              >
                <Card sx={{ 
                  borderRadius: 2, 
                  '&:hover': { boxShadow: 4 },
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                  <CardContent sx={{ 
                    p: { xs: 2, sm: 3 },
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                  }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Box
                        sx={{
                          backgroundColor: color,
                          p: { xs: 1, sm: 1.5 },
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <IconComponent sx={{ 
                          color: 'white', 
                          fontSize: { xs: 24, sm: 28 } 
                        }} />
                      </Box>
                      
                    </Box>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        mb: 0.5,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}
                    >
                      {kpi.title}
                    </Typography>
                    <Typography 
                      variant={isMobile ? 'h6' : 'h5'} 
                      sx={{ 
                        fontWeight: 700, 
                        color: 'text.primary',
                        fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }
                      }}
                    >
                      {kpi.value}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            );
          })}
        </Box>

        {/* Charts Row 1 */}
        <Box
          sx={{
            display: 'grid',
            gap: { xs: 2, md: 3 },
            mb: { xs: 2, sm: 3 },
            gridTemplateColumns: {
              xs: 'repeat(1, minmax(0, 1fr))',
              lg: 'repeat(2, minmax(0, 1fr))',
            },
            gridAutoRows: { xs: 'minmax(400px, auto)', md: 'minmax(450px, auto)' },
            alignItems: 'stretch',
          }}
        >
          {/* Monthly Revenue Trend */}
          <Box sx={{ display: 'flex', width: '100%', height: '100%', minHeight: { xs: 400, md: 450 } }}>
            <Card sx={{ 
              borderRadius: 2, 
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}>
              <CardContent sx={{ 
                p: { xs: 2, sm: 3 },
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}>
                <Typography 
                  variant={isMobile ? 'subtitle1' : 'h6'} 
                  sx={{ 
                    fontWeight: 600, 
                    mb: { xs: 2, sm: 3 }, 
                    color: 'text.primary',
                    fontSize: { xs: '1rem', sm: '1.25rem' }
                  }}
                >
                  Monthly Revenue-Expense Trend
                </Typography>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  {monthlyRevenue && monthlyRevenue.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart 
                        data={monthlyRevenue}
                        margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis 
                          dataKey={monthlyRevenue[0]?.monthName ? "monthName" : "month"} 
                          stroke="#64748b"
                          tick={{ fontSize: isMobile ? 10 : 12 }}
                        />
                        <YAxis 
                          stroke="#64748b"
                          width={isMobile ? 60 : 80}
                          tick={{ fontSize: isMobile ? 10 : 12 }}
                          tickFormatter={(value) => {
                            if (value >= 1000000) {
                              return `₺${(value / 1000000).toFixed(1)}M`;
                            } else if (value >= 1000) {
                              return `₺${(value / 1000).toFixed(0)}K`;
                            }
                            return `₺${value.toFixed(0)}`;
                          }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#fff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="revenue"
                          stroke="#3b82f6"
                          strokeWidth={3}
                          name="Revenue"
                        />
                        <Line
                          type="monotone"
                          dataKey="expense"
                          stroke="#ef4444"
                          strokeWidth={3}
                          name="Expense"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                      <Typography color="text.secondary">No data found</Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Top Customers */}
          <Box sx={{ display: 'flex', width: '100%', height: '100%', minHeight: { xs: 400, md: 450 } }}>
            <Card sx={{ 
              borderRadius: 2, 
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}>
              <CardContent sx={{ 
                p: { xs: 2, sm: 3 },
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}>
                <Typography 
                  variant={isMobile ? 'subtitle1' : 'h6'} 
                  sx={{ 
                    fontWeight: 600, 
                    mb: { xs: 2, sm: 3 }, 
                    color: 'text.primary',
                    fontSize: { xs: '1rem', sm: '1.25rem' }
                  }}
                >
                  Customer Distribution
                </Typography>
                <Box sx={{ 
                  flex: 1, 
                  minHeight: 0, 
                  display: 'flex', 
                  flexDirection: 'column',
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  {customerRevenue && customerRevenue.length > 0 ? (
                    <>
                     
                     
                      {/* Pie Chart */}
                      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={customerRevenue}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={false}
                              outerRadius={isMobile ? '60%' : isTablet ? '70%' : '80%'}
                              innerRadius={isMobile ? '20%' : '30%'}
                              fill="#8884d8"
                              dataKey="value"
                              paddingAngle={2}
                            >
                              {customerRevenue.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip 
                              formatter={(value) => formatCurrency(value)}
                              contentStyle={{
                                backgroundColor: '#fff',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                padding: '8px',
                                fontSize: isMobile ? '12px' : '14px'
                              }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                    </>
                  ) : (
                    <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                      <Typography color="text.secondary">No data found</Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Charts Row 2 */}
        <Box
          sx={{
            display: 'grid',
            gap: { xs: 2, md: 3 },
            mb: { xs: 2, sm: 3 },
            gridTemplateColumns: {
              xs: 'repeat(1, minmax(0, 1fr))',
              lg: 'repeat(2, minmax(0, 1fr))',
            },
            gridAutoRows: { xs: 'minmax(400px, auto)', md: 'minmax(450px, auto)' },
            alignItems: 'stretch',
          }}
        >
          {/* Document Types */}
          <Box sx={{ display: 'flex', width: '100%', height: '100%', minHeight: { xs: 400, md: 450 } }}>
            <Card sx={{ 
              borderRadius: 2, 
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}>
              <CardContent sx={{ 
                p: { xs: 2, sm: 3 },
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}>
                <Typography 
                  variant={isMobile ? 'subtitle1' : 'h6'} 
                  sx={{ 
                    fontWeight: 600, 
                    mb: { xs: 2, sm: 3 }, 
                    color: 'text.primary',
                    fontSize: { xs: '1rem', sm: '1.25rem' }
                  }}
                >
                  Document Type Analysis
                </Typography>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  {documentTypes && documentTypes.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart 
                        data={documentTypes}
                        margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis 
                          dataKey="type" 
                          stroke="#64748b"
                          tick={{ fontSize: isMobile ? 10 : 12 }}
                          angle={isMobile ? -45 : 0}
                          textAnchor={isMobile ? "end" : "middle"}
                          height={isMobile ? 80 : 60}
                        />
                        <YAxis 
                          stroke="#64748b"
                          width={isMobile ? 60 : 80}
                          tick={{ fontSize: isMobile ? 10 : 12 }}
                          tickFormatter={(value) => {
                            if (value >= 1000000) {
                              return `₺${(value / 1000000).toFixed(1)}M`;
                            } else if (value >= 1000) {
                              return `₺${(value / 1000).toFixed(0)}K`;
                            }
                            return `₺${value.toFixed(0)}`;
                          }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#fff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Legend />
                        <Bar dataKey="amount" fill="#3b82f6" name="Amount" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                      <Typography color="text.secondary">No data found</Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Top Customer Details Table */}
          <Box sx={{ display: 'flex', width: '100%', height: '100%', minHeight: { xs: 400, md: 450 } }}>
            <Card sx={{ 
              borderRadius: 2, 
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}>
              <CardContent sx={{ 
                p: { xs: 2, sm: 3 },
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}>
                <Typography 
                  variant={isMobile ? 'subtitle1' : 'h6'} 
                  sx={{ 
                    fontWeight: 600, 
                    mb: { xs: 2, sm: 3 }, 
                    color: 'text.primary',
                    fontSize: { xs: '1rem', sm: '1.25rem' }
                  }}
                >
                  {selectedCustomer === 'all' ? 'Top 5 Customer Details' : 'Customer Transaction Details'}
                </Typography>
                <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                  <TableContainer sx={{ height: '100%', overflowX: 'auto' }}>
                    <Table size={isMobile ? 'small' : 'medium'}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ 
                            fontWeight: 600,
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            whiteSpace: 'nowrap'
                          }}>
                            {selectedCustomer === 'all' ? 'Customer' : 'Transaction'}
                          </TableCell>
                          <TableCell 
                            align="right" 
                            sx={{ 
                              fontWeight: 600,
                              fontSize: { xs: '0.75rem', sm: '0.875rem' }
                            }}
                          >
                            Count
                          </TableCell>
                          <TableCell 
                            align="right" 
                            sx={{ 
                              fontWeight: 600,
                              fontSize: { xs: '0.75rem', sm: '0.875rem' }
                            }}
                          >
                            Total
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {topCustomerDetails.length > 0 ? (
                          topCustomerDetails.map((customer, idx) => (
                            <TableRow key={idx} hover>
                              <TableCell sx={{ 
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: { xs: 150, sm: 'none' }
                              }}>
                                {customer.name}
                              </TableCell>
                              <TableCell 
                                align="right"
                                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                              >
                                {customer.count}
                              </TableCell>
                              <TableCell 
                                align="right" 
                                sx={{ 
                                  fontWeight: 600,
                                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                }}
                              >
                                {formatCurrency(customer.value)}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                              <Typography color="text.secondary">
                                No data found
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EDashboard;

